import { createColumnHelper } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { DataTable, dataTableFeatures } from "@/components/ui/data-table";
import { exportHref, type components } from "@jbnl/api-client";

type KorpsOption = components["schemas"]["KorpsOption"];

const helper = createColumnHelper<typeof dataTableFeatures, KorpsOption>();

/**
 * Plaats is a column, not a parenthesis: it is what tells two "Jeugdbrandweer Noord" apart. Built
 * per render, because a module constant is laid down before i18next has a catalog.
 */
const kolommen = (t: TFunction<"ploegen">) =>
  helper.columns([
    helper.accessor("name", {
      header: t("korpsen.korps"),
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    helper.accessor("city", { header: t("korpsen.plaats") }),
  ]);

/**
 * Only ever drawn for a regiocontact: a korpscontact holds one korps, and App picks it without
 * asking. The search box earns its place at a regio's worth of korpsen, not at one.
 */
export function KorpsenTable({
  korpsen,
  korpsId,
  onKies,
  page,
  onPageChange,
}: {
  korpsen: KorpsOption[];
  korpsId: string | undefined;
  onKies: (korpsId: string) => void;
  page: number | undefined;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation("ploegen");

  return (
    <DataTable
      label={t("korpsen.label")}
      data={korpsen}
      columns={kolommen(t)}
      getRowId={(korps) => korps.id}
      selectedId={korpsId}
      onSelect={onKies}
      searchPlaceholder={t("korpsen.zoek")}
      empty={t("korpsen.leeg")}
      exportHref={exportHref("/api/me/korpsen/export")}
      clientPageSize={25}
      page={page}
      onPageChange={onPageChange}
    />
  );
}
